import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  Clock, 
  CalendarOff, 
  RefreshCw, 
  Plus, 
  Trash2, 
  PlayCircle, 
  PauseCircle,
  Sparkles,
  Ban
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { formatTimeRange } from "@/utils/timeFormat";

const DAYS_OF_WEEK = [
  { value: 0, label: "Monday", short: "Mon" },
  { value: 1, label: "Tuesday", short: "Tue" },
  { value: 2, label: "Wednesday", short: "Wed" },
  { value: 3, label: "Thursday", short: "Thu" },
  { value: 4, label: "Friday", short: "Fri" },
  { value: 5, label: "Saturday", short: "Sat" },
  { value: 6, label: "Sunday", short: "Sun" },
];

const EnhancedAvailability = ({ API, token, userId, onSlotsGenerated }) => {
  const [recurringSchedules, setRecurringSchedules] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [showAddSchedule, setShowAddSchedule] = useState(false);
  const [showAddBlock, setShowAddBlock] = useState(false);
  const [showGenerateSlots, setShowGenerateSlots] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [newSchedule, setNewSchedule] = useState({
    days_of_week: [],
    start_time: "09:00",
    end_time: "17:00",
    effective_from: new Date().toISOString().split('T')[0],
    effective_until: "",
    name: "",
  });
  
  const [newBlock, setNewBlock] = useState({
    start_date: "",
    end_date: "",
    reason: "",
    all_day: true,
  });
  
  const [generateDates, setGenerateDates] = useState({
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  });
  
  const [previewSlots, setPreviewSlots] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Use XMLHttpRequest to avoid body stream issues
  const apiRequest = (url, options = {}) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(options.method || 'GET', url, true);
      if (options.headers) {
        Object.keys(options.headers).forEach(key => {
          xhr.setRequestHeader(key, options.headers[key]);
        });
      }
      xhr.timeout = 15000;
      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
        } catch (e) {
          resolve({ ok: false, status: xhr.status, data: { detail: 'Invalid server response' } });
        }
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.ontimeout = () => reject(new Error('Request timed out'));
      xhr.send(options.body || null);
    });
  };

  useEffect(() => {
    fetchRecurringSchedules();
    fetchBlockedDates();
  }, []);

  const fetchRecurringSchedules = async () => {
    try {
      const { ok, data } = await apiRequest(`${API}/availability/recurring`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (ok) {
        setRecurringSchedules(data);
      }
    } catch (error) {
      console.error("Failed to fetch recurring schedules:", error);
    }
  };

  const fetchBlockedDates = async () => {
    try {
      const { ok, data } = await apiRequest(`${API}/availability/blocked`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (ok) {
        setBlockedDates(data);
      }
    } catch (error) {
      console.error("Failed to fetch blocked dates:", error);
    }
  };

  const handleDayToggle = (dayValue) => {
    setNewSchedule(prev => {
      const days = prev.days_of_week.includes(dayValue)
        ? prev.days_of_week.filter(d => d !== dayValue)
        : [...prev.days_of_week, dayValue].sort((a, b) => a - b);
      return { ...prev, days_of_week: days };
    });
  };

  const handleCreateSchedule = async (e) => {
    e.preventDefault();
    if (newSchedule.days_of_week.length === 0) {
      toast.error("Please select at least one day");
      return;
    }
    
    setLoading(true);
    try {
      const { ok, data } = await apiRequest(`${API}/availability/recurring`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...newSchedule,
          effective_until: newSchedule.effective_until || null,
        }),
      });

      if (ok) {
        toast.success("Recurring schedule created!");
        setShowAddSchedule(false);
        setNewSchedule({
          days_of_week: [],
          start_time: "09:00",
          end_time: "17:00",
          effective_from: new Date().toISOString().split('T')[0],
          effective_until: "",
          name: "",
        });
        fetchRecurringSchedules();
      } else {
        toast.error(data.detail || "Failed to create schedule");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      const { ok } = await apiRequest(`${API}/availability/recurring/${scheduleId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (ok) {
        toast.success("Schedule deleted");
        fetchRecurringSchedules();
      } else {
        toast.error("Failed to delete schedule");
      }
    } catch (error) {
      toast.error("An error occurred");
    }
  };

  const handleToggleSchedule = async (scheduleId) => {
    try {
      const { ok, data } = await apiRequest(`${API}/availability/recurring/${scheduleId}/toggle`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (ok) {
        toast.success(data.message);
        fetchRecurringSchedules();
      } else {
        toast.error("Failed to toggle schedule");
      }
    } catch (error) {
      toast.error("An error occurred");
    }
  };

  const handleCreateBlock = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { ok, data } = await apiRequest(`${API}/availability/blocked`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newBlock),
      });

      if (ok) {
        toast.success("Dates blocked successfully!");
        setShowAddBlock(false);
        setNewBlock({
          start_date: "",
          end_date: "",
          reason: "",
          all_day: true,
        });
        fetchBlockedDates();
        if (onSlotsGenerated) onSlotsGenerated(); // Refresh availability as blocking deletes slots
      } else {
        toast.error(data.detail || "Failed to block dates");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBlock = async (blockId) => {
    try {
      const { ok } = await apiRequest(`${API}/availability/blocked/${blockId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (ok) {
        toast.success("Block removed");
        fetchBlockedDates();
      } else {
        toast.error("Failed to remove block");
      }
    } catch (error) {
      toast.error("An error occurred");
    }
  };

  const handleGenerateSlots = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { ok, data } = await apiRequest(`${API}/availability/generate-slots`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(generateDates),
      });

      if (ok) {
        toast.success(data.message);
        setShowGenerateSlots(false);
        if (onSlotsGenerated) onSlotsGenerated();
      } else {
        toast.error(data.detail || "Failed to generate slots");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to set quick date ranges
  const setQuickDateRange = (days) => {
    const start = new Date();
    const end = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
    setGenerateDates({
      start_date: start.toISOString().split('T')[0],
      end_date: end.toISOString().split('T')[0],
    });
    setShowPreview(false);
    setPreviewSlots([]);
  };

  // Generate preview of slots without actually creating them
  const generatePreview = () => {
    setLoadingPreview(true);
    
    try {
      const startDate = new Date(generateDates.start_date);
      const endDate = new Date(generateDates.end_date);
      
      if (endDate < startDate) {
        toast.error("End date must be after start date");
        setLoadingPreview(false);
        return;
      }
      
      const activeSchedules = recurringSchedules.filter(s => s.is_active);
      if (activeSchedules.length === 0) {
        toast.error("No active schedules to preview");
        setLoadingPreview(false);
        return;
      }
      
      // Create a set of blocked dates
      const blockedDateSet = new Set();
      blockedDates.forEach(block => {
        const blockStart = new Date(block.start_date);
        const blockEnd = new Date(block.end_date);
        let current = new Date(blockStart);
        while (current <= blockEnd) {
          blockedDateSet.add(current.toISOString().split('T')[0]);
          current.setDate(current.getDate() + 1);
        }
      });
      
      // Generate preview slots
      const preview = [];
      let currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        const dateStr = currentDate.toISOString().split('T')[0];
        const dayOfWeek = (currentDate.getDay() + 6) % 7; // Convert to Mon=0, Sun=6
        
        // Skip blocked dates
        if (!blockedDateSet.has(dateStr)) {
          activeSchedules.forEach(schedule => {
            if (schedule.days_of_week.includes(dayOfWeek)) {
              // Check effective dates
              const effectiveFrom = new Date(schedule.effective_from);
              if (currentDate >= effectiveFrom) {
                if (!schedule.effective_until || currentDate <= new Date(schedule.effective_until)) {
                  preview.push({
                    date: dateStr,
                    day: DAYS_OF_WEEK[dayOfWeek].short,
                    start_time: schedule.start_time,
                    end_time: schedule.end_time,
                    schedule_name: schedule.name || "Untitled"
                  });
                }
              }
            }
          });
        }
        
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      setPreviewSlots(preview);
      setShowPreview(true);
    } catch (error) {
      toast.error("Failed to generate preview");
    } finally {
      setLoadingPreview(false);
    }
  };

  const formatDays = (days) => {
    if (days.length === 0) return "No days selected";
    if (days.length === 7) return "Every day";
    if (JSON.stringify(days) === JSON.stringify([0,1,2,3,4])) return "Weekdays (Mon-Fri)";
    if (JSON.stringify(days) === JSON.stringify([5,6])) return "Weekends";
    return days.map(d => DAYS_OF_WEEK.find(day => day.value === d)?.short).join(", ");
  };

  return (
    <div className="space-y-6">
      {/* Recurring Schedules Section */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <RefreshCw className="w-5 h-5 text-blue-600" />
                Recurring Schedules
              </CardTitle>
              <CardDescription className="mt-1">
                Set up your regular working hours. Slots will be auto-generated based on these rules.
              </CardDescription>
            </div>
            <Button
              data-testid="add-recurring-schedule-btn"
              onClick={() => setShowAddSchedule(true)}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Schedule
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {recurringSchedules.length === 0 ? (
            <p className="text-slate-500 text-center py-6 text-sm">
              No recurring schedules yet. Add one to automate your availability!
            </p>
          ) : (
            <div className="space-y-3">
              {recurringSchedules.map((schedule) => (
                <div
                  key={schedule.schedule_id}
                  data-testid={`schedule-${schedule.schedule_id}`}
                  className={`border rounded-lg p-4 ${schedule.is_active ? 'border-blue-200 bg-blue-50/50' : 'border-slate-200 bg-slate-50 opacity-60'}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-slate-900">
                          {schedule.name || "Untitled Schedule"}
                        </span>
                        {!schedule.is_active && (
                          <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">
                            Paused
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {formatTimeRange(schedule.start_time, schedule.end_time)}
                        </span>
                        <span>{formatDays(schedule.days_of_week)}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        From {schedule.effective_from}
                        {schedule.effective_until ? ` to ${schedule.effective_until}` : " (ongoing)"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        data-testid={`toggle-schedule-${schedule.schedule_id}`}
                        onClick={() => handleToggleSchedule(schedule.schedule_id)}
                        variant="ghost"
                        size="sm"
                        className={schedule.is_active ? "text-amber-600 hover:text-amber-700" : "text-emerald-600 hover:text-emerald-700"}
                      >
                        {schedule.is_active ? (
                          <PauseCircle className="w-4 h-4" />
                        ) : (
                          <PlayCircle className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        data-testid={`delete-schedule-${schedule.schedule_id}`}
                        onClick={() => handleDeleteSchedule(schedule.schedule_id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Blocked Dates Section */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <CalendarOff className="w-5 h-5 text-red-600" />
                Blocked Dates
              </CardTitle>
              <CardDescription className="mt-1">
                Block vacations, holidays, or any dates you're unavailable.
              </CardDescription>
            </div>
            <Button
              data-testid="add-blocked-date-btn"
              onClick={() => setShowAddBlock(true)}
              size="sm"
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              <Ban className="w-4 h-4 mr-1" />
              Block Dates
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {blockedDates.length === 0 ? (
            <p className="text-slate-500 text-center py-6 text-sm">
              No blocked dates. Your entire schedule is available!
            </p>
          ) : (
            <div className="space-y-2">
              {blockedDates.map((block) => (
                <div
                  key={block.block_id}
                  data-testid={`block-${block.block_id}`}
                  className="border border-red-200 bg-red-50/50 rounded-lg p-3 flex justify-between items-center"
                >
                  <div>
                    <p className="font-medium text-slate-900 text-sm">
                      {block.start_date === block.end_date 
                        ? block.start_date 
                        : `${block.start_date} to ${block.end_date}`}
                    </p>
                    {block.reason && (
                      <p className="text-xs text-slate-600">{block.reason}</p>
                    )}
                  </div>
                  <Button
                    data-testid={`delete-block-${block.block_id}`}
                    onClick={() => handleDeleteBlock(block.block_id)}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-100"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generate Slots Button */}
      <Card className="border-emerald-200 bg-emerald-50/30">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-medium text-slate-900">Generate Availability Slots</h3>
                <p className="text-sm text-slate-600">
                  Create slots from your recurring schedules for a date range
                </p>
              </div>
            </div>
            <Button
              data-testid="generate-slots-btn"
              onClick={() => setShowGenerateSlots(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Generate Slots
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Add Recurring Schedule Dialog */}
      <Dialog open={showAddSchedule} onOpenChange={setShowAddSchedule}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Recurring Schedule</DialogTitle>
            <DialogDescription>
              Set up regular working hours that repeat weekly
            </DialogDescription>
          </DialogHeader>
          
          {/* Quick Templates Section */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs font-medium text-blue-800 mb-2 flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              Quick Templates
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                data-testid="template-weekday-9to5"
                onClick={() => setNewSchedule({
                  ...newSchedule,
                  name: "Weekday 9-5",
                  days_of_week: [0, 1, 2, 3, 4],
                  start_time: "09:00",
                  end_time: "17:00"
                })}
                className="px-3 py-1.5 bg-white border border-blue-200 rounded-md text-xs font-medium text-blue-700 hover:bg-blue-50 hover:border-blue-300 transition-all shadow-sm"
              >
                Weekday 9-5
              </button>
              <button
                type="button"
                data-testid="template-morning-shift"
                onClick={() => setNewSchedule({
                  ...newSchedule,
                  name: "Morning Shift",
                  days_of_week: [0, 1, 2, 3, 4],
                  start_time: "06:00",
                  end_time: "12:00"
                })}
                className="px-3 py-1.5 bg-white border border-blue-200 rounded-md text-xs font-medium text-blue-700 hover:bg-blue-50 hover:border-blue-300 transition-all shadow-sm"
              >
                Morning Shift
              </button>
              <button
                type="button"
                data-testid="template-afternoon"
                onClick={() => setNewSchedule({
                  ...newSchedule,
                  name: "Afternoon",
                  days_of_week: [0, 1, 2, 3, 4],
                  start_time: "12:00",
                  end_time: "18:00"
                })}
                className="px-3 py-1.5 bg-white border border-blue-200 rounded-md text-xs font-medium text-blue-700 hover:bg-blue-50 hover:border-blue-300 transition-all shadow-sm"
              >
                Afternoon
              </button>
              <button
                type="button"
                data-testid="template-evening"
                onClick={() => setNewSchedule({
                  ...newSchedule,
                  name: "Evening Hours",
                  days_of_week: [0, 1, 2, 3, 4],
                  start_time: "17:00",
                  end_time: "21:00"
                })}
                className="px-3 py-1.5 bg-white border border-blue-200 rounded-md text-xs font-medium text-blue-700 hover:bg-blue-50 hover:border-blue-300 transition-all shadow-sm"
              >
                Evening Hours
              </button>
              <button
                type="button"
                data-testid="template-weekend"
                onClick={() => setNewSchedule({
                  ...newSchedule,
                  name: "Weekend Only",
                  days_of_week: [5, 6],
                  start_time: "10:00",
                  end_time: "16:00"
                })}
                className="px-3 py-1.5 bg-white border border-blue-200 rounded-md text-xs font-medium text-blue-700 hover:bg-blue-50 hover:border-blue-300 transition-all shadow-sm"
              >
                Weekend Only
              </button>
              <button
                type="button"
                data-testid="template-all-day"
                onClick={() => setNewSchedule({
                  ...newSchedule,
                  name: "Full Day",
                  days_of_week: [0, 1, 2, 3, 4, 5, 6],
                  start_time: "08:00",
                  end_time: "20:00"
                })}
                className="px-3 py-1.5 bg-white border border-blue-200 rounded-md text-xs font-medium text-blue-700 hover:bg-blue-50 hover:border-blue-300 transition-all shadow-sm"
              >
                Full Day (All Week)
              </button>
            </div>
          </div>

          <form onSubmit={handleCreateSchedule} className="space-y-4">
            <div>
              <Label htmlFor="schedule_name">Schedule Name (optional)</Label>
              <Input
                id="schedule_name"
                data-testid="schedule-name-input"
                value={newSchedule.name}
                onChange={(e) => setNewSchedule({ ...newSchedule, name: e.target.value })}
                placeholder="e.g., Regular Work Hours"
                className="mt-2"
              />
            </div>
            
            <div>
              <Label>Days of Week *</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {DAYS_OF_WEEK.map((day) => (
                  <button
                    key={day.value}
                    type="button"
                    data-testid={`day-${day.short.toLowerCase()}`}
                    onClick={() => handleDayToggle(day.value)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                      newSchedule.days_of_week.includes(day.value)
                        ? "bg-blue-600 text-white"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    }`}
                  >
                    {day.short}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setNewSchedule({ ...newSchedule, days_of_week: [0,1,2,3,4] })}
                  className="text-xs text-blue-600 hover:underline"
                >
                  Weekdays
                </button>
                <button
                  type="button"
                  onClick={() => setNewSchedule({ ...newSchedule, days_of_week: [5,6] })}
                  className="text-xs text-blue-600 hover:underline"
                >
                  Weekends
                </button>
                <button
                  type="button"
                  onClick={() => setNewSchedule({ ...newSchedule, days_of_week: [0,1,2,3,4,5,6] })}
                  className="text-xs text-blue-600 hover:underline"
                >
                  All days
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_time">Start Time *</Label>
                <Input
                  id="start_time"
                  data-testid="schedule-start-time-input"
                  type="time"
                  required
                  value={newSchedule.start_time}
                  onChange={(e) => setNewSchedule({ ...newSchedule, start_time: e.target.value })}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="end_time">End Time *</Label>
                <Input
                  id="end_time"
                  data-testid="schedule-end-time-input"
                  type="time"
                  required
                  value={newSchedule.end_time}
                  onChange={(e) => setNewSchedule({ ...newSchedule, end_time: e.target.value })}
                  className="mt-2"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="effective_from">Effective From *</Label>
                <Input
                  id="effective_from"
                  data-testid="schedule-from-input"
                  type="date"
                  required
                  value={newSchedule.effective_from}
                  onChange={(e) => setNewSchedule({ ...newSchedule, effective_from: e.target.value })}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="effective_until">Until (optional)</Label>
                <Input
                  id="effective_until"
                  data-testid="schedule-until-input"
                  type="date"
                  value={newSchedule.effective_until}
                  onChange={(e) => setNewSchedule({ ...newSchedule, effective_until: e.target.value })}
                  className="mt-2"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddSchedule(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                data-testid="save-schedule-btn"
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                {loading ? "Creating..." : "Create Schedule"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Blocked Dates Dialog */}
      <Dialog open={showAddBlock} onOpenChange={setShowAddBlock}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Block Dates</DialogTitle>
            <DialogDescription>
              Block dates when you're unavailable (vacation, holidays, etc.)
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateBlock} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="block_start">From Date *</Label>
                <Input
                  id="block_start"
                  data-testid="block-start-input"
                  type="date"
                  required
                  value={newBlock.start_date}
                  onChange={(e) => setNewBlock({ ...newBlock, start_date: e.target.value })}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="block_end">To Date *</Label>
                <Input
                  id="block_end"
                  data-testid="block-end-input"
                  type="date"
                  required
                  value={newBlock.end_date}
                  onChange={(e) => setNewBlock({ ...newBlock, end_date: e.target.value })}
                  className="mt-2"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="block_reason">Reason (optional)</Label>
              <Input
                id="block_reason"
                data-testid="block-reason-input"
                value={newBlock.reason}
                onChange={(e) => setNewBlock({ ...newBlock, reason: e.target.value })}
                placeholder="e.g., Vacation, Holiday, Personal"
                className="mt-2"
              />
            </div>

            <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
              <p className="text-sm text-amber-900">
                <strong>Note:</strong> Blocking dates will automatically remove any existing available slots within that range.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddBlock(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                data-testid="save-block-btn"
                type="submit"
                disabled={loading}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {loading ? "Blocking..." : "Block Dates"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Generate Slots Dialog */}
      <Dialog open={showGenerateSlots} onOpenChange={(open) => {
        setShowGenerateSlots(open);
        if (!open) {
          setShowPreview(false);
          setPreviewSlots([]);
        }
      }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Generate Availability Slots</DialogTitle>
            <DialogDescription>
              Create availability slots from your active recurring schedules
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleGenerateSlots} className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
              <p className="text-sm text-blue-900">
                Slots will be created based on your <strong>active</strong> recurring schedules, excluding any blocked dates.
              </p>
            </div>

            {/* Quick Range Buttons */}
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-3">
              <p className="text-xs font-medium text-emerald-800 mb-2 flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Quick Select
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid="quick-next-week"
                  onClick={() => setQuickDateRange(7)}
                  className="px-3 py-1.5 bg-white border border-emerald-200 rounded-md text-xs font-medium text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 transition-all shadow-sm"
                >
                  Next Week
                </button>
                <button
                  type="button"
                  data-testid="quick-two-weeks"
                  onClick={() => setQuickDateRange(14)}
                  className="px-3 py-1.5 bg-white border border-emerald-200 rounded-md text-xs font-medium text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 transition-all shadow-sm"
                >
                  Next 2 Weeks
                </button>
                <button
                  type="button"
                  data-testid="quick-month"
                  onClick={() => setQuickDateRange(30)}
                  className="px-3 py-1.5 bg-white border border-emerald-200 rounded-md text-xs font-medium text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 transition-all shadow-sm"
                >
                  Next Month
                </button>
                <button
                  type="button"
                  data-testid="quick-two-months"
                  onClick={() => setQuickDateRange(60)}
                  className="px-3 py-1.5 bg-white border border-emerald-200 rounded-md text-xs font-medium text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 transition-all shadow-sm"
                >
                  Next 2 Months
                </button>
                <button
                  type="button"
                  data-testid="quick-quarter"
                  onClick={() => setQuickDateRange(90)}
                  className="px-3 py-1.5 bg-white border border-emerald-200 rounded-md text-xs font-medium text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 transition-all shadow-sm"
                >
                  Next Quarter (90 days)
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="gen_start">From Date *</Label>
                <Input
                  id="gen_start"
                  data-testid="generate-start-input"
                  type="date"
                  required
                  value={generateDates.start_date}
                  onChange={(e) => {
                    setGenerateDates({ ...generateDates, start_date: e.target.value });
                    setShowPreview(false);
                  }}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="gen_end">To Date *</Label>
                <Input
                  id="gen_end"
                  data-testid="generate-end-input"
                  type="date"
                  required
                  value={generateDates.end_date}
                  onChange={(e) => {
                    setGenerateDates({ ...generateDates, end_date: e.target.value });
                    setShowPreview(false);
                  }}
                  className="mt-2"
                />
              </div>
            </div>

            {/* Preview Button */}
            <div className="flex justify-center">
              <Button
                type="button"
                data-testid="preview-slots-btn"
                onClick={generatePreview}
                disabled={loadingPreview || recurringSchedules.filter(s => s.is_active).length === 0}
                variant="outline"
                className="text-blue-600 border-blue-300 hover:bg-blue-50"
              >
                {loadingPreview ? "Calculating..." : "Preview What Will Be Created"}
              </Button>
            </div>

            {/* Preview Results */}
            {showPreview && (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-slate-100 px-3 py-2 border-b border-slate-200">
                  <p className="text-sm font-medium text-slate-700">
                    Preview: {previewSlots.length} slot{previewSlots.length !== 1 ? 's' : ''} will be created
                  </p>
                </div>
                {previewSlots.length === 0 ? (
                  <div className="p-4 text-center text-slate-500 text-sm">
                    No slots will be created for this date range. Check your schedule settings.
                  </div>
                ) : (
                  <div className="max-h-48 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-slate-600 font-medium">Date</th>
                          <th className="px-3 py-2 text-left text-slate-600 font-medium">Day</th>
                          <th className="px-3 py-2 text-left text-slate-600 font-medium">Time</th>
                          <th className="px-3 py-2 text-left text-slate-600 font-medium">Schedule</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {previewSlots.slice(0, 50).map((slot, idx) => (
                          <tr key={idx} className="hover:bg-slate-50">
                            <td className="px-3 py-2 text-slate-900">{slot.date}</td>
                            <td className="px-3 py-2 text-slate-600">{slot.day}</td>
                            <td className="px-3 py-2 text-slate-600">{formatTimeRange(slot.start_time, slot.end_time)}</td>
                            <td className="px-3 py-2 text-slate-500">{slot.schedule_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {previewSlots.length > 50 && (
                      <div className="px-3 py-2 bg-slate-50 text-xs text-slate-500 text-center">
                        + {previewSlots.length - 50} more slots...
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <p className="text-xs text-slate-500">
              Maximum 90 days at a time. Existing slots won't be duplicated.
            </p>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowGenerateSlots(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                data-testid="confirm-generate-btn"
                type="submit"
                disabled={loading || recurringSchedules.filter(s => s.is_active).length === 0}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {loading ? "Generating..." : `Generate ${showPreview ? previewSlots.length : ''} Slots`}
              </Button>
            </div>
            
            {recurringSchedules.filter(s => s.is_active).length === 0 && (
              <p className="text-xs text-amber-600 text-center">
                You need at least one active recurring schedule to generate slots.
              </p>
            )}
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EnhancedAvailability;
